<script setup>
import { ref } from 'vue'
import { getI18n } from '@/plugins/i18n'

import { DxDataGrid } from 'devextreme-vue/data-grid'

import * as dataGrid from '@/composables/data-grid'
import * as dataGridColumn from '@/composables/data-grid-column'
import { useRegistryStore } from '@/stores/registry.store'

import CustomerDropDownBox from '@/components/shared/CustomerDropDownBox.vue'
import DepositDropDownBox from '@/components/shared/DepositDropDownBox.vue'
import SupplierDropDownBox from '@/components/shared/SupplierDropDownBox.vue'

import CustomToolbarAddButtonTemplate from '@/components/shared/CustomToolbarAddButtonTemplate.vue'
import EditLockButtonsTemplate from '@/components/shared/EditLockButtonsTemplate.vue'

const props = defineProps({
  subject: {
    type: Object,
    required: true
  },
  subagency: {
    type: Boolean,
    default: false
  },
  customerId: {
    type: Number,
    required: false
  },
  supplierRef: {
    type: Number,
    required: false
  },
  entityLock: {
    type: Object,
    required: true
  },
})

const subjectRefId = props.subject.id

const gridId = 'subject-profile-grid'

const i18n = getI18n()
const registryStore = useRegistryStore()
const { gridRef } = dataGrid.useGridRef()
const currentSupplierRef = ref(props.supplierRef)

const gridDataSource = dataGrid.useODataSource(registryStore.api.getAbsoluteUrl() + registryStore.api.subjectProfilesUrl,
  { filter: [props.subagency ? 'supplier_ref' : 'customer', '=', props.subagency ? props.subject.supplier_ref : props.subject.customer_id] },
)

const supplierOrCustomerColumn = props.subagency
  ? dataGridColumn.useCustomerColumn({
    editCellTemplate: 'customerEditor',
  })
  : dataGridColumn.useSupplierColumn({
    supplierRef: currentSupplierRef,
    editCellTemplate: 'supplierEditor',
  })

const rowButtons = props.entityLock.getEntityLockButtons()

const columns = [
  {
    type: 'buttons',
    buttons: [{template: 'editButtonsTemplate'}],
    width: '6rem',
    fixed: false
  },
  supplierOrCustomerColumn,
  dataGridColumn.useSupplierBrandColumn({
    validationRules: [{ type: 'required' }],
    setCellValue: (newData, value) => { newData.supplier_brand_ref = value },
  }),
  dataGridColumn.useSupplierDivisionColumn({ supplierRef: currentSupplierRef }),
  dataGridColumn.useDepositColumn({ visible: true }),
  {
    dataField: 'corresponding_code',
    caption: i18n.t('common.correspondingCode'),
    validationRules: [{ type: 'required' }],
  }
]

const { defaultActionTitle, defaultItemTitle } = dataGrid.useDefaultDialogTitle()

const dialogTitle = (i) => {
  let title = `${defaultActionTitle(i)} ${props.subagency ? i18n.t('common.subAgency') : i18n.t('common.profile')}`
  if (i.id) {
    title += ` ${i.code}`
  }
  const defaultItemTitleValue = defaultItemTitle(props.templateData?.data)
  if (defaultItemTitleValue) {
    title += ` ${defaultItemTitleValue}`
  }
  return title
}

const editItems = [
  { dataField: props.subagency ? 'customer' : 'supplier_ref' },
  { dataField: 'supplier_division_ref', editorOptions: { showClearButton: true } },
  { dataField: 'supplier_brand_ref' },
  { dataField: 'deposit' },
  { dataField: 'corresponding_code' },
]

const editing = dataGrid.useDefaultEditing(dialogTitle, {
  items: [
    {
      itemType: 'group',
      caption: i18n.t('common.mainInfo'),
      colCount: 5,
      items: editItems,
    },
  ]
}, {
  allowUpdating: (e) => !props.entityLock?.isLocked(false, subjectRefId),
  allowDeleting: (e) => !props.entityLock?.isLocked(false, subjectRefId),
  onInitializedCustom: (e) => props.entityLock.readOnlyToolbarItems(e),
})

const onInitNewRow = (e) => {
  e.data.deposit = registryStore.companyData.deposit
  if (props.subagency) {
    e.data.supplier_ref = props.subject.supplier_ref
  } else {
    e.data.customer = props.subject.customer_id
  }
}

const onEditorPreparing = async(e) => {
  if (e.parentType === 'dataRow' && e.dataField === 'supplier_division_ref') {
    e.editorOptions.disabled = !props.subagency && !e.row?.data?.supplier_ref
  }
}

const gridOptions = dataGrid.useDefaultGridOptions({
  id: gridId,
  dataSource: gridDataSource,
  columns: columns,
  editing: editing,
  paging: { enabled: false },
  pager: { visible: false },
  columnChooser: { enabled: false },
  onEditorPreparing: onEditorPreparing,
  onInitNewRow: onInitNewRow,
  onToolbarPreparing: (e) => dataGrid.useDefaultToolbar(e, {}),
  height: '100%',
})

</script>
<template>
  <div>
    <dx-data-grid
      ref="gridRef"
      v-bind="gridOptions"
      class="q-mb-lg"
    >
      <template #depositEditor="{ data: cellInfo }">
        <deposit-drop-down-box
          :data="cellInfo"
        />
      </template>
      <template #customerEditor="{ data: cellInfo }">
        <customer-drop-down-box
          :exclude="props.subject.customer_id"
          :data="cellInfo"
        />
      </template>
      <template #supplierEditor="{ data: cellInfo }">
        <supplier-drop-down-box
          :exclude="props.subject.supplier_ref"
          :data="cellInfo"
        />
      </template>
      <template
        #editButtonsTemplate="{ data: cellInfo }"
      >
        <edit-lock-buttons-template
          :entity-name="'Subject'"
          :master-entity-id="subjectRefId"
          :cell-info="cellInfo"
          :grid="gridRef"
          :entity-lock="props.entityLock"
          :buttons="rowButtons"
        />
      </template>
      <template #customToolbarAddButtonTemplate>
        <custom-toolbar-add-button-template
          :master-entity-id="subjectRefId"
          :grid="gridRef"
          :entity-lock="props.entityLock"
        />
      </template>
    </dx-data-grid>
  </div>
</template>
